import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { Contracts as ContractsIcon } from "@dreamhub/designsystem/icons";
import PageLayout from "@/pages/settings/components/PageLayout";
import { ContractsSettingsTabs } from "@/pages/settings/types";
import { FieldManagementTab } from "@/pages/settings/components/dealsSettings/FieldManagement/FieldManagementTab";
import OnboardingStudioTab from "@/pages/settings/components/contractsSettings/onboardingStudio/OnboardingStudioTab";
import { GeneralTab } from "@/pages/settings/components/contractsSettings/GeneralTab";
import { EntityType } from "@/services/hooks/entities/useEntityType";
import { useFeatureFlag } from "@/services/features/hook";
import { FeatureFlags } from "@/services/features/types";

const TRANSLATION_KEY = "settings.contracts";
const TRANSLATION_KEY_TABS = "settings.contracts.tabs";

export const ContractsSettings: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { enabled: fxRatesEnabled, loading: fxRatesLoading } = useFeatureFlag(
    FeatureFlags.CRM_11075_CONTRACT_FX_RATES
  );
  // The General tab only holds the FX rate refresh setting so far, so it
  // exists only while that feature is on.
  const isTabAvailable = (tab: ContractsSettingsTabs) =>
    tab !== ContractsSettingsTabs.GENERAL || fxRatesEnabled;

  const [tabValue, setTabValue] = useState<ContractsSettingsTabs>(() => {
    const tabParam = searchParams.get("tab");
    return Object.values(ContractsSettingsTabs).includes(
      tabParam as ContractsSettingsTabs
    )
      ? (tabParam as ContractsSettingsTabs)
      : ContractsSettingsTabs.ONBOARDING_STUDIO;
  });

  // A ?tab=general link is kept while the flag loads, and dropped for the
  // default tab once the flag turns out to be off.
  useEffect(() => {
    if (
      !fxRatesLoading &&
      !fxRatesEnabled &&
      tabValue === ContractsSettingsTabs.GENERAL
    ) {
      setTabValue(ContractsSettingsTabs.ONBOARDING_STUDIO);
    }
  }, [fxRatesLoading, fxRatesEnabled, tabValue]);

  useEffect(() => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.set("tab", tabValue);
      return params;
    });
  }, [tabValue, setSearchParams]);

  const getTabOptions = () => {
    const options: Record<string, string> = {};
    Object.values(ContractsSettingsTabs)
      .filter(isTabAvailable)
      .forEach((tab) => {
        options[tab] = t(`${TRANSLATION_KEY_TABS}.${tab}`);
      });
    return options;
  };

  const renderTabContent = () => {
    switch (tabValue) {
      case ContractsSettingsTabs.GENERAL:
        return fxRatesEnabled ? <GeneralTab /> : <OnboardingStudioTab />;
      case ContractsSettingsTabs.FIELD_MANAGEMENT:
        return <FieldManagementTab entityType={EntityType.CONTRACT} />;
      case ContractsSettingsTabs.ONBOARDING_STUDIO:
        return <OnboardingStudioTab />;
      default:
        return <OnboardingStudioTab />;
    }
  };

  return (
    <PageLayout
      data-test="settings-contracts-page"
      title={t(`${TRANSLATION_KEY}.title`)}
      logoIcon={<ContractsIcon />}
      tabs={{
        options: getTabOptions(),
        defaultValue: tabValue,
        valueSelected: (newTabValue: string) =>
          setTabValue(newTabValue as ContractsSettingsTabs)
      }}
    >
      {renderTabContent()}
    </PageLayout>
  );
};
